import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShieldCheck, SlidersHorizontal, ChatText, X, Check, ArrowUpRight } from '@phosphor-icons/react';
import { WEB3FORMS_ACCESS_KEY } from '../utils/web3forms';
import '../styles/privacy-contact-widget.css';

const CONSENT_STORAGE_KEY = 'aurablox_privacy_consent_v1';

const DEFAULT_PREFS = {
  necessary: true,
  analytics: true,
  functional: true,
  doNotSellOrShare: false,
  contactPreference: 'project-only', // 'project-only' | 'updates' | 'opt-out-all'
};

const loadStoredConsent = () => {
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const saveStoredConsent = (prefs) => {
  try {
    const payload = {
      ...prefs,
      necessary: true,
      updatedAt: new Date().toISOString(),
    };
    window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(payload));
    return payload;
  } catch {
    return prefs;
  }
};

const PrivacyAndContactWidget = () => {
  const { pathname } = useLocation();
  const [hasConsented, setHasConsented] = useState(true); // start true until mounted on client
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);
  const [activeModal, setActiveModal] = useState(null); // null | 'privacy' | 'contact'
  const [optOutEmail, setOptOutEmail] = useState('');
  const [optOutStatus, setOptOutStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [savedToast, setSavedToast] = useState(false);

  useEffect(() => {
    const stored = loadStoredConsent();
    const gpcEnabled = Boolean(navigator.globalPrivacyControl);
    if (stored) {
      setPrefs({
        ...DEFAULT_PREFS,
        ...stored,
        necessary: true,
      });
      setHasConsented(true);
    } else {
      if (gpcEnabled) {
        setPrefs((prev) => ({
          ...prev,
          analytics: false,
          doNotSellOrShare: true,
        }));
      }
      setHasConsented(false);
    }

    const handleOpenPrivacy = () => setActiveModal('privacy');
    const handleOpenContact = () => setActiveModal('contact');
    window.addEventListener('aurablox:open-privacy-preferences', handleOpenPrivacy);
    window.addEventListener('aurablox:open-contact-preferences', handleOpenContact);
    return () => {
      window.removeEventListener('aurablox:open-privacy-preferences', handleOpenPrivacy);
      window.removeEventListener('aurablox:open-contact-preferences', handleOpenContact);
    };
  }, []);

  useEffect(() => {
    if (!activeModal) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setActiveModal(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [activeModal]);

  const triggerToast = () => {
    setSavedToast(true);
    window.setTimeout(() => setSavedToast(false), 2600);
  };

  const handleAcceptAll = () => {
    const updated = saveStoredConsent({
      ...prefs,
      necessary: true,
      analytics: true,
      functional: true,
      doNotSellOrShare: false,
    });
    setPrefs(updated);
    setHasConsented(true);
    setActiveModal(null);
    triggerToast();
  };

  const handleOptOutAll = () => {
    const updated = saveStoredConsent({
      ...prefs,
      necessary: true,
      analytics: false,
      functional: false,
      doNotSellOrShare: true,
      contactPreference: 'opt-out-all',
    });
    setPrefs(updated);
    setHasConsented(true);
    setActiveModal(null);
    triggerToast();
  };

  const handleSaveCustom = (e) => {
    if (e) e.preventDefault();
    const updated = saveStoredConsent(prefs);
    setPrefs(updated);
    setHasConsented(true);
    setActiveModal(null);
    triggerToast();
  };

  const handleContactPreferenceSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setOptOutStatus(null);
    saveStoredConsent(prefs);
    setHasConsented(true);

    if (!optOutEmail.trim()) {
      setSubmitting(false);
      setOptOutStatus({
        type: 'ok',
        message: 'Your browser contact and opt-out preferences have been saved locally.',
      });
      return;
    }

    try {
      const formData = new FormData();
      formData.append('access_key', WEB3FORMS_ACCESS_KEY);
      formData.append('subject', `AURABLOX Contact Preference / Opt-Out (${prefs.contactPreference})`);
      formData.append('email', optOutEmail.trim());
      formData.append(
        'message',
        `Contact Preference: ${prefs.contactPreference}\nDo Not Sell/Share Opt-Out: ${prefs.doNotSellOrShare ? 'Yes' : 'No'}\nAnalytics: ${prefs.analytics ? 'Enabled' : 'Opted Out'}`
      );
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: formData,
      });
      if (!res.ok) throw new Error('Request failed');
      setOptOutStatus({
        type: 'ok',
        message: `Preferences recorded for ${optOutEmail.trim()}.`,
      });
      setOptOutEmail('');
    } catch {
      setOptOutStatus({
        type: 'err',
        message: 'Saved locally. To update email records directly, write to jjohnson@aurablox.com.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* ─── SUBTLE FLOATING ICON TRIGGER (BOTTOM-LEFT) ─── */}
      <div className="ab-floating-dock" aria-label="Privacy and contact preferences">
        <button
          type="button"
          className="ab-floating-icon-btn"
          onClick={() => setActiveModal('privacy')}
          aria-haspopup="dialog"
          aria-expanded={Boolean(activeModal)}
          aria-label="Privacy settings, opt-out, and contact preferences"
          title="Privacy, Opt-Out & Contact Preferences"
        >
          <ShieldCheck size={18} weight="regular" className="ab-floating-icon-btn__svg" aria-hidden="true" />
        </button>
      </div>

      {/* ─── SAVED CONFIRMATION TOAST ─── */}
      {savedToast && (
        <div className="ab-prefs-toast" role="status" aria-live="polite">
          <Check size={15} weight="bold" aria-hidden="true" />
          <span>Privacy &amp; opt-out preferences saved</span>
        </div>
      )}

      {/* ─── FIRST-USE BOTTOM PRIVACY BANNER ─── */}
      {!hasConsented && (
        <aside
          className="ab-privacy-banner"
          role="region"
          aria-labelledby="ab-privacy-banner-title"
        >
          <div className="ab-privacy-banner__inner">
            <div className="ab-privacy-banner__copy">
              <div className="ab-privacy-banner__eyebrow">
                <span className="ab-privacy-banner__seal" aria-hidden="true" />
                <span>PRIVACY SETTINGS &amp; CONSENT PREFERENCES</span>
              </div>
              <h2 id="ab-privacy-banner-title" className="ab-privacy-banner__title">
                Your data and communication preferences stay in your control.
              </h2>
              <p className="ab-privacy-banner__desc">
                We use essential storage to keep this site reliable and remember your choices, plus optional
                analytics to understand which engineering pages are useful. You can accept all, opt out of
                non-essential tracking and sharing, or configure granular preferences at any time.
              </p>
            </div>

            <div className="ab-privacy-banner__actions">
              <button
                type="button"
                className="ab-privacy-banner__btn ab-privacy-banner__btn--ghost"
                onClick={() => setActiveModal('privacy')}
              >
                <SlidersHorizontal size={15} weight="bold" aria-hidden="true" />
                <span>Privacy Settings</span>
              </button>
              <button
                type="button"
                className="ab-privacy-banner__btn ab-privacy-banner__btn--secondary"
                onClick={handleOptOutAll}
              >
                Opt Out Non-Essential
              </button>
              <button
                type="button"
                className="ab-privacy-banner__btn ab-privacy-banner__btn--primary"
                onClick={handleAcceptAll}
              >
                Accept All
              </button>
            </div>
          </div>
        </aside>
      )}

      {/* ─── MODAL: PRIVACY SETTINGS, COOKIES & OPT-OUT ─── */}
      {activeModal && (
        <div
          className="ab-prefs-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="ab-prefs-modal-title"
          onClick={() => setActiveModal(null)}
        >
          <div
            className="ab-prefs-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="ab-prefs-modal__header">
              <div className="ab-prefs-modal__tabs" role="tablist" aria-label="Preference categories">
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeModal === 'privacy'}
                  className={`ab-prefs-modal__tab${activeModal === 'privacy' ? ' is-active' : ''}`}
                  onClick={() => setActiveModal('privacy')}
                >
                  01 / Privacy &amp; Opt-Out
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeModal === 'contact'}
                  className={`ab-prefs-modal__tab${activeModal === 'contact' ? ' is-active' : ''}`}
                  onClick={() => setActiveModal('contact')}
                >
                  02 / Contact Preferences
                </button>
              </div>
              <button
                type="button"
                className="ab-prefs-modal__close"
                onClick={() => setActiveModal(null)}
                aria-label="Close preferences dialog"
              >
                <X size={18} weight="bold" aria-hidden="true" />
              </button>
            </header>

            {activeModal === 'privacy' ? (
              <div className="ab-prefs-modal__body">
                <div className="ab-prefs-modal__intro">
                  <span className="ab-prefs-modal__kicker">AURABLOX PRIVACY GOVERNANCE</span>
                  <h2 id="ab-prefs-modal-title">Privacy &amp; Opt-Out Preferences</h2>
                  <p>
                    Manage how AURABLOX stores preferences and processes technical telemetry. We never sell client
                    or visitor personal data, and we honor Global Privacy Control (GPC) signals automatically.
                  </p>
                </div>

                <div className="ab-prefs-list">
                  <div className="ab-prefs-item">
                    <div className="ab-prefs-item__info">
                      <div className="ab-prefs-item__head">
                        <strong>Strictly Necessary Storage</strong>
                        <span className="ab-prefs-badge ab-prefs-badge--locked">ALWAYS ACTIVE</span>
                      </div>
                      <p>
                        Required for core site navigation, form security (bot protection), and storing your privacy
                        and opt-out choices locally in your browser.
                      </p>
                    </div>
                  </div>

                  <label className="ab-prefs-item" htmlFor="pref-functional">
                    <div className="ab-prefs-item__info">
                      <div className="ab-prefs-item__head">
                        <strong>Functional &amp; UI Preferences</strong>
                      </div>
                      <p>
                        Remembers non-identifying interface state such as interactive diagram steps and rotating
                        editorial artwork order across page visits.
                      </p>
                    </div>
                    <input
                      id="pref-functional"
                      type="checkbox"
                      className="ab-prefs-toggle"
                      checked={prefs.functional}
                      onChange={(e) => setPrefs({ ...prefs, functional: e.target.checked })}
                    />
                  </label>

                  <label className="ab-prefs-item" htmlFor="pref-analytics">
                    <div className="ab-prefs-item__info">
                      <div className="ab-prefs-item__head">
                        <strong>Performance &amp; Analytics</strong>
                      </div>
                      <p>
                        Helps us understand aggregate page visits and load performance so we can improve technical
                        documentation and product demos.
                      </p>
                    </div>
                    <input
                      id="pref-analytics"
                      type="checkbox"
                      className="ab-prefs-toggle"
                      checked={prefs.analytics && !prefs.doNotSellOrShare}
                      disabled={prefs.doNotSellOrShare}
                      onChange={(e) => setPrefs({ ...prefs, analytics: e.target.checked })}
                    />
                  </label>

                  <label className="ab-prefs-item ab-prefs-item--highlight" htmlFor="pref-optout">
                    <div className="ab-prefs-item__info">
                      <div className="ab-prefs-item__head">
                        <strong>Global Opt-Out (Do Not Sell or Share / GPC)</strong>
                        <span className="ab-prefs-badge">CCPA / CPRA / GDPR</span>
                      </div>
                      <p>
                        Enabling this immediately disables all non-essential analytics and records a strict
                        opt-out against any third-party data sharing.
                      </p>
                    </div>
                    <input
                      id="pref-optout"
                      type="checkbox"
                      className="ab-prefs-toggle"
                      checked={prefs.doNotSellOrShare}
                      onChange={(e) =>
                        setPrefs({
                          ...prefs,
                          doNotSellOrShare: e.target.checked,
                          analytics: e.target.checked ? false : prefs.analytics,
                        })
                      }
                    />
                  </label>
                </div>

                <footer className="ab-prefs-modal__footer">
                  <button
                    type="button"
                    className="ab-privacy-banner__btn ab-privacy-banner__btn--secondary"
                    onClick={handleOptOutAll}
                  >
                    Opt Out All Non-Essential
                  </button>
                  <div className="ab-prefs-modal__footer-right">
                    <button
                      type="button"
                      className="ab-privacy-banner__btn ab-privacy-banner__btn--ghost"
                      onClick={handleAcceptAll}
                    >
                      Accept All
                    </button>
                    <button
                      type="button"
                      className="ab-privacy-banner__btn ab-privacy-banner__btn--primary"
                      onClick={handleSaveCustom}
                    >
                      Save My Preferences
                    </button>
                  </div>
                </footer>
              </div>
            ) : (
              <div className="ab-prefs-modal__body">
                <div className="ab-prefs-modal__intro">
                  <span className="ab-prefs-modal__kicker">DIRECT CHANNELS &amp; COMMUNICATION OPT-OUT</span>
                  <h2 id="ab-prefs-modal-title">Contact &amp; Communication Preferences</h2>
                  <p>
                    Reach the AURABLOX engineering team directly, or update how and when we may contact you by email.
                  </p>
                </div>

                <div className="ab-contact-quick-grid">
                  {pathname !== '/contact' && (
                    <Link
                      to="/contact#inquiry"
                      className="ab-contact-quick-card"
                      onClick={() => setActiveModal(null)}
                    >
                      <span className="ab-contact-quick-card__label">PROJECT SCOPING</span>
                      <strong>Open Full Inquiry Form</strong>
                      <span>Share your stack, timeline, or audit scope with our team.</span>
                      <span className="ab-contact-quick-card__cta">
                        Go to form <ArrowUpRight size={14} weight="bold" aria-hidden="true" />
                      </span>
                    </Link>
                  )}
                  <a
                    href="mailto:jjohnson@aurablox.com?subject=AURABLOX%20Engineering%20Inquiry"
                    className="ab-contact-quick-card"
                  >
                    <span className="ab-contact-quick-card__label">DIRECT EMAIL</span>
                    <strong>jjohnson@aurablox.com</strong>
                    <span>Write directly to Josh Johnson (Co-Founder &amp; CEO).</span>
                    <span className="ab-contact-quick-card__cta">
                      Compose email <ArrowUpRight size={14} weight="bold" aria-hidden="true" />
                    </span>
                  </a>
                </div>

                <form className="ab-contact-prefs-form" onSubmit={handleContactPreferenceSubmit}>
                  <span className="ab-prefs-modal__kicker">EMAIL COMMUNICATION &amp; OPT-OUT REGISTRY</span>
                  <div className="ab-contact-prefs-options" role="radiogroup" aria-label="Communication frequency">
                    <label className="ab-contact-radio">
                      <input
                        type="radio"
                        name="contactPreference"
                        value="project-only"
                        checked={prefs.contactPreference === 'project-only'}
                        onChange={(e) => setPrefs({ ...prefs, contactPreference: e.target.value })}
                      />
                      <div>
                        <strong>Direct Project Replies Only (Default)</strong>
                        <span>Only contact me in response to a specific inquiry or active engagement.</span>
                      </div>
                    </label>
                    <label className="ab-contact-radio">
                      <input
                        type="radio"
                        name="contactPreference"
                        value="updates"
                        checked={prefs.contactPreference === 'updates'}
                        onChange={(e) => setPrefs({ ...prefs, contactPreference: e.target.value })}
                      />
                      <div>
                        <strong>Engineering Releases &amp; Architecture Notes</strong>
                        <span>Occasional updates when AURABLOX ships new open-source tools or case studies.</span>
                      </div>
                    </label>
                    <label className="ab-contact-radio ab-contact-radio--optout">
                      <input
                        type="radio"
                        name="contactPreference"
                        value="opt-out-all"
                        checked={prefs.contactPreference === 'opt-out-all'}
                        onChange={(e) =>
                          setPrefs({
                            ...prefs,
                            contactPreference: e.target.value,
                            doNotSellOrShare: true,
                          })
                        }
                      />
                      <div>
                        <strong>Opt Out of All Outbound Communications</strong>
                        <span>Remove my email from all non-transactional outreach and marketing lists.</span>
                      </div>
                    </label>
                  </div>

                  <div className="ab-contact-prefs-submit-row">
                    <input
                      type="email"
                      value={optOutEmail}
                      onChange={(e) => setOptOutEmail(e.target.value)}
                      placeholder="your.email@company.com (optional for registry)"
                      aria-label="Email address for communication preference or opt-out"
                      className="ab-contact-prefs-input"
                    />
                    <button
                      type="submit"
                      className="ab-privacy-banner__btn ab-privacy-banner__btn--primary"
                      disabled={submitting}
                    >
                      {submitting ? 'Saving…' : 'Update Preferences'}
                    </button>
                  </div>

                  {optOutStatus && (
                    <p
                      role="status"
                      className={`ab-contact-prefs-status ab-contact-prefs-status--${optOutStatus.type}`}
                    >
                      {optOutStatus.message}
                    </p>
                  )}
                </form>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default PrivacyAndContactWidget;
